<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { required } from "../../../../utils/validation";
import {
  Currency,
  Customer,
  GeneralLedger,
  Item,
  RecordStatus,
  SalesRep,
  Warehouse,
} from "../../../../types/models";
import {
  documentActions,
  downloadPDFBlob,
  formatDate,
  formatNumber,
  isEmpty,
  printPDF,
  printPDFBlob,
  sharePDFBlob,
} from "../../../../utils/CommonFunctions";
import MessageBox from "../../../../services/MessageBox";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import DateInput from "../../../../components/ui/form/DateInput.vue";
import RadioInput from "../../../../components/ui/form/RadioInput.vue";

import { useScreenSize } from "../../../../utils/useScreenSize";
import { tokenManager } from "../../../../api/utils/tokenManager";
import ItemSalesGrid from "../../../../components/ui/Templates/ItemSalesGrid.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import TabComponent from "../../../../components/ui/form/TabComponent.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import SelectSalesReps from "../../../../components/ui/Templates/SelectSalesReps.vue";
import TextAreaInput from "../../../../components/ui/form/TextAreaInput.vue";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  recordStatus?: RecordStatus[];
  cartItems?: Item[];
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save"): void;
  (e: "viewDocument", data: any, title: string, routePath: string): void;
}>();

const { isMobile } = useScreenSize();
const isReadOnly = ref<boolean>(props.mode === "view");

const mobileTabs = [
  { name: "customer", label: "Customer" },
  { name: "delivery", label: "Delivery" },
  { name: "document", label: "Summary" },
];

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  setFormData,
  resetForm,
} = useFormValidation({
  sales_quotations_id: props.data?.sales_quotations_id || "",
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  due_date: props.data?.due_date || new Date().toISOString().split("T")[0],
  customer: props.data?.customer,
  document_no: props.data?.document_no || "",
  customers_id: props.data?.customers_id || "",
  prefix: props.data?.prefix || "",
  currency:
    props.data?.currency ||
    tokenManager.getSetting("default_currency", true) ||
    "USD",
  exchange_rate: props.data?.exchange_rate || 1,
  currencys_id: props.data?.currencys_id || 1,
  ref_no: props.data?.ref_no || "",
  uid: props.data?.uid || "",
  download: false,
  warehouses_id:
    props.data?.warehouses_id ||
    tokenManager.getSetting("default_warehouse", true),
  price_lists_id:
    props.data?.price_lists_id ||
    tokenManager.getSetting("default_price_list", true),
  credit_terms_id:
    props.data?.credit_terms_id ||
    tokenManager.getSetting("default_credit_term", true),
  customer_reference: props.data?.customer_reference || "",
  remarks: props.data?.remarks || "",
  status_id: props.data?.status_id || "",
  sales_orders_id: props.data?.sales_orders_id || "",
  sales_goods_deliverys_id: props.data?.sales_goods_deliverys_id || "",
  sales_channels_id:
    props.data?.sales_channels_id ||
    tokenManager.getSetting("default_sales_channel", true),
  outlets_id: props.data?.outlets_id || "",
  delivery_date:
    props.data?.delivery_date || new Date().toISOString().split("T")[0],
  delivery_address_id: props.data?.delivery_address_id || "",
  delivery_address: props.data?.delivery_address || "",
  sales_reps_id: props.data?.sales_reps_id || "",
  shift_end_reports_id: props.data?.shift_end_reports_id || "",
  outlet_pos_stations_id: props.data?.outlet_pos_stations_id || "",
  attachment: props.data?.attachment || "",
  to_deliver: props.data?.to_deliver || false,
  items: props.data?.items || ([] as Item[]),
  accounting: props.data?.accounting || ([] as GeneralLedger[]),
  delivery_tracking: props.data?.delivery_tracking || "",
  credit_term: props.data?.credit_term || "",
  quotation: null as any,
  invoice: null as any,
  delivery: null as any,
  order: null as any,
});

const customer = ref<Customer>({} as Customer);
const warehouse = ref<Warehouse>();
const showSelectSalesReps = ref(false);

setFieldRules("customers_id", [required("Select Customer")]);
setFieldRules("warehouses_id", [required("Select Warehouse")]);
setFieldRules("price_lists_id", [required("Select Price List")]);
setFieldRules("credit_terms_id", [required("Payment Term is required")]);
setFieldRules("sales_channels_id", [required("Sales Channel is required")]);

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setFormData(response.data);
    customer.value = response.data.customer;
  }
};

const validateGrid = async (): Promise<boolean> => {
  for (const item of formData.value.items) {
    if (item.tracked_by === "Batch") {
      const batch_qty = item.batches?.reduce(
        (sum: number, batch: { quantity: number }) => {
          if (typeof batch.quantity === "number") {
            return sum + batch.quantity;
          }
          return sum;
        },
        0
      );
      if (batch_qty !== item.inventory_qty) {
        return false;
      }
    } else if (item.tracked_by === "Serial") {
      const serial_qty = item.serials?.reduce(
        (sum: number, serial: { quantity: number }) => {
          if (typeof serial.quantity === "number") {
            return sum + serial.quantity;
          }
          return sum;
        },
        0
      );
      if (serial_qty !== item.inventory_qty) {
        return false;
      }
    }
  }
  return true;
};

const handleSubmit = async (
  new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (validateForm()) {
    const isValid = await validateGrid();
    if (!isValid) {
      MessageBox.show(
        "There are some Batch/Serial Quantity differences",
        "warning"
      );
      return;
    }
    let items = formData.value.items.filter((e: any) => e.items_id != "");
    if (items.length <= 0) {
      MessageBox.show("Select at least 1 item", "warning");
      return;
    }
    formData.value.download = download || share || print;
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "create",
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show("saved successfully");
      emit("save");
      if (new_record) {
        clearForm();
      } else if (download) {
        downloadPDFBlob(
          response.data.file,
          "Sales Quotation #: " + response.data.data.document_no
        );
      } else if (mail) {
        if (!response.data.error) {
          Toast.show(
            "Sales Quotation #: " +
              response.data.data.document_no +
              " emailed successfully"
          );
        }
      } else if (share) {
        sharePDFBlob(
          response.data.file,
          "Sales Quotation #: " + response.data.data.document_no
        );
      } else if (print) {
        printPDFBlob(response.data.file);
      }
      setFormData(response.data.data);
      customer.value = response.data.customer;
    }
  }
};

const clearForm = () => {
  resetForm();

  fields.value.date_time.value = formatDate(new Date());
  fields.value.due_date.value = formatDate(new Date());
  fields.value.warehouses_id.value = tokenManager.getSetting(
    "default_warehouse",
    true
  );
  fields.value.price_lists_id.value =
    tokenManager.getSetting("default_price_list");
  fields.value.credit_terms_id.value = tokenManager.getSetting(
    "default_credit_term",
    true
  );
  fields.value.sales_channels_id.value = tokenManager.getSetting(
    "default_sales_channel",
    true
  );

  customer.value = {} as Customer;
  isReadOnly.value = false;

  nextTick(() => {
    const input = document.getElementById("customers_id");
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

const allowNewItemRow = ref(false);
const handleCustomer = (data: Customer) => {
  customer.value = data;
  if (!isEmpty(data.price_lists_id))
    fields.value.price_lists_id.value = data.price_lists_id;
  if (!isEmpty(data.credit_terms_id))
    fields.value.credit_terms_id.value = data.credit_terms_id;
  if (!isEmpty(data.sales_reps_id))
    fields.value.sales_reps_id.value = data.sales_reps_id;

  if (fields.value.warehouses_id.value == "")
    fields.value.warehouses_id.value = tokenManager.getSetting(
      "default_warehouse",
      true
    );
  if (fields.value.sales_channels_id.value == "")
    fields.value.sales_channels_id.value = tokenManager.getSetting(
      "default_sales_channel",
      true
    );
  if (
    customer.value &&
    customer.value.addresses &&
    customer.value.addresses.length > 0
  ) {
    fields.value.delivery_address_id.value = customer.value.addresses[0].id;
    fields.value.delivery_address.value =
      customer.value.addresses[0].full_address;
  }
  prepareItemRow();
};

const handleWarehouse = (data: any) => {
  warehouse.value = data;
  prepareItemRow();
};

const updateSalesRep = async (rep: SalesRep) => {
  if (!rep) return;
  let params = {
    id: formData.value?.uid,
    sales_reps_id: rep.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "assign-sales-rep",
    params
  );
  if (response.success == true) {
    setFormData(response.data);
    Toast.show("Sales Rep Assigned", "info");
    showSelectSalesReps.value = false;
  } else {
    Toast.show("No Sales Reps found", "warning");
  }
};

const handlePaymetTerms = (data: any) => {
  if (data.credit_days > 0) {
    const today = new Date();
    today.setDate(today.getDate() + data.credit_days);
    fields.value.due_date.value = formatDate(today);
  } else {
    fields.value.due_date.value = formatDate(new Date());
  }
  prepareItemRow();
};
const prepareItemRow = () => {
  if (
    !isEmpty(formData.value.customers_id) &&
    !isEmpty(formData.value.warehouses_id) &&
    !isEmpty(formData.value.price_lists_id) &&
    !isEmpty(formData.value.credit_terms_id)
  ) {
    if (props.cartItems) prepareCartItems();
    allowNewItemRow.value = true;
  } else {
    allowNewItemRow.value = false;
  }
};

const prepareCartItems = async () => {
  const ids = props.cartItems?.map((item) => item.id);
  const response = await api.post(
    "clients/inventory/item-management/items/search",
    {
      ids: ids,
      price_lists_id: formData.value.price_lists_id,
      warehouses_id: formData.value.warehouses_id,
    }
  );
  if (response.success == true) {
    if (itemGridRef.value) {
      itemGridRef.value.handleSelectedItems(response.data);
    }
  }
};

const activeTab = ref("items");
const activeTabMobile = ref("customer");

const assignSalesRep = () => {
  showSelectSalesReps.value = true;
};

function warehouseLoaded() {
  if (props.mode == "create") {
    fields.value.warehouses_id.value = tokenManager.getSetting(
      "default_warehouse",
      true
    );
  }
}
function priceListLoaded() {
  if (props.mode == "create") {
    fields.value.price_lists_id.value = tokenManager.getSetting(
      "default_price_list",
      true
    );
  }
}
function salesChannelLoaded() {
  if (props.mode == "create") {
    fields.value.sales_channels_id.value = tokenManager.getSetting(
      "default_sales_channel",
      true
    );
  }
}
function creditTermsLoaded() {
  if (props.mode == "create") {
    fields.value.credit_terms_id.value = tokenManager.getSetting(
      "default_credit_term",
      true
    );
  }
}

const itemGridRef = ref<InstanceType<typeof ItemSalesGrid> | null>(null);
const handleNewItemRow = () => {
  if (itemGridRef.value) {
    itemGridRef.value.addItemRow(-1);
  }
};

const convertDocuments = async (doc_type: string) => {
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "convert",
    {
      id: formData.value.uid,
      convert: doc_type,
    }
  );
  if (response.success == true) {
    MessageBox.confirm({
      title: doc_type + " Created",
      message:
        doc_type +
        " #(" +
        response.data.data.document_no +
        ") created successfully",
      confirmText: "Print " + doc_type,
      cancelText: "Close",
      extraButton: {
        text: "View " + doc_type,
        action: () => {
          if (doc_type == "Sales Order")
            formData.value.order = response.data.data;
          else if (doc_type == "Goods Delivery")
            formData.value.delivery = response.data.data;
          else if (doc_type == "Sales Invoice")
            formData.value.invoice = response.data.data;
          viewDocument(doc_type);
        },
      },
    }).then((confirmed) => {
      if (confirmed) {
        printPDF(response.data.file);
      }
    });
  }
};

const viewDocument = (doc_type: string) => {
  if (doc_type == "Sales Order" && formData.value.order)
    emit(
      "viewDocument",
      formData.value.order,
      "Sales Order",
      "revenue/sales/sales-orders"
    );
  else if (doc_type == "Goods Delivery" && formData.value.delivery)
    emit(
      "viewDocument",
      formData.value.delivery,
      "Sales Goods Delivery",
      "revenue/sales/sales-goods-deliverys"
    );
  else if (doc_type == "Sales Invoice" && formData.value.invoice)
    emit(
      "viewDocument",
      formData.value.invoice,
      "Sales Invoice",
      "revenue/sales/sales-invoices"
    );
};

const updateItems = (items: Item[] | []) => {
  formData.value.items = items;
};

const handleActions = async (action: string) => {
  console.log("actions", action);
  documentActions(
    props.endPoint,
    formData.value.uid,
    action,
    "Sales Quotation# " + formData.value.document_no
  );
};

const showForm = ref(false);
onMounted(() => {
  showForm.value = true;
  isReadOnly.value = props.mode == "view";
  if (props.mode != "create") {
    getRecord();
  } else {
    clearForm();
  }
});

const currency = ref<Currency>();
const currencys = ref<Currency[]>([]);
const updateExchangeRate = (curr: Currency) => {
  currency.value = curr;
  if (curr && curr.exchange_rates && curr.exchange_rates.length > 0) {
    fields.value.exchange_rate.value = curr.exchange_rates[0].rate;
  } else {
    fields.value.exchange_rate.value = 1;
  }
};

const currencyLoaded = (data: any[]) => {
  currencys.value = data;
  if (data && data.length > 0 && data[0].exchange_rates.lenght > 0) {
    currency.value = data[0];
    fields.value.currencys_id.value = data[0].id;
    fields.value.exchange_rate.value = data[0].exchange_rates[0].rate;
  } else {
    fields.value.currencys_id.value = 1;
    fields.value.exchange_rate.value = 1;
  }
};
</script>

<template>
  <ModalFormComponent width="100%" @close="$emit('close')">
    <template #header>
      <!-- Header -->
      <DocumentHeader
        :title="title"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-drop-down="isReadOnly"
        @close="$emit('close')"
        :permissions="permissions"
        @clear="clearForm"
        @new="clearForm"
        @save="handleSubmit(false)"
        @download="handleSubmit(false, true)"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      >
        <template #dropdownbuttons>
          <button
            @click="assignSalesRep"
            v-if="
              !data?.sales_reps_id && !formData.invoice && !formData.delivery
            "
            class="block w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <span>👨‍💼</span>
            Assign Sales Rep
          </button>
          <hr />
          <button
            @click="convertDocuments('Sales Order')"
            v-if="!formData.invoice && !formData.delivery && !formData.order"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="ClipboardEdit" />
            Create Sales Order
          </button>
          <button
            @click="convertDocuments('Goods Delivery')"
            v-if="!formData.invoice && !formData.delivery && !formData.order"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="Truck" />
            Create Goods Delivery
          </button>
          <button
            @click="convertDocuments('Sales Invoice')"
            v-if="!formData.invoice && !formData.delivery && !formData.order"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="Receipt" />
            Create Invoice
          </button>
          <button
            @click="viewDocument('Sales Order')"
            v-else-if="formData.order"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="FileCheck" />
            View Sales Order ({{ formData.order?.document_no }})
          </button>
          <button
            @click="viewDocument('Goods Delivery')"
            v-else-if="formData.delivery"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="Truck" />
            View Delivery ({{ formData.delivery?.document_no }})
          </button>
          <button
            @click="viewDocument('Sales Invoice')"
            v-else-if="formData.invoice"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="Receipt" />
            View Invoice ({{ formData.invoice?.document_no }})
          </button>
        </template>
      </DocumentHeader>
    </template>
    <!-- Main Content - Scrollable -->
    <template #form>
      <!--header-->
      <template v-if="!isMobile">
        <div class="grid md:grid-cols-5 gap-6 mb-8">
          <!-- Left Column -->
          <div class="space-y-6 md:col-span-3">
            <div class="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <div class="">
                  <SearchSelect
                    label="Customer"
                    end-point="revenue/master-data/customers"
                    v-model="fields.customers_id.value"
                    v-if="!isReadOnly"
                    :readonly="isReadOnly"
                    id="customers_id"
                    :remote="true"
                    :withDetails="true"
                    :allow-new-record="true"
                    :error="
                      fields.customers_id.touched
                        ? fields.customers_id.error
                        : null
                    "
                    @blur="fields.customers_id.touched = true"
                    @select="handleCustomer"
                  />
                </div>

                <div
                  v-if="customer"
                  class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ customer?.name }}
                  </h4>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ customer?.email }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ customer?.mobile }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ customer?.address ?? customer?.building_address }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    <span>TIN: </span> {{ customer?.tax_registration_no }}
                    <span
                      :style="{
                        float: 'right',
                        color:
                          (Number(customer?.credit_limit) || 0) -
                            (Number(customer?.outstanding) || 0) <
                          0
                            ? 'red'
                            : 'green',
                      }"
                    >
                      Credit Balance:
                      {{
                        formatNumber(
                          (Number(customer?.credit_limit) || 0) -
                            (Number(customer?.outstanding) || 0)
                        )
                      }}
                    </span>
                  </p>
                </div>
                <div
                  v-else
                  class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 text-center"
                >
                  No Customer selected
                </div>
                <div class="md:grid md:grid-cols-2 md:gap-4">
                  <SearchSelect
                    label="Currency"
                    end-point="finance/master-data/currency"
                    v-model="fields.currencys_id.value"
                    :readonly="isReadOnly"
                    @select="updateExchangeRate"
                    @loaded="currencyLoaded"
                    :error="
                      fields.currencys_id.touched
                        ? fields.currencys_id.error
                        : null
                    "
                    @blur="fields.currencys_id.touched = true"
                  />
                  <NumberInput
                    label="Exchange Rate"
                    :readonly="isReadOnly"
                    v-model="fields.exchange_rate.value"
                    :show-controls="false"
                    :error="
                      fields.exchange_rate.touched
                        ? fields.exchange_rate.error
                        : null
                    "
                    @blur="fields.exchange_rate.touched = true"
                  />
                </div>
              </div>
              <div class="space-y-6">
                <div class="grid grid-cols-5 gap-6 mb-8">
                  <div
                    class="flex justify-between items-center mb-2 col-span-1"
                  >
                    <CheckInput
                      v-model="fields.to_deliver.value"
                      :readonly="isReadOnly"
                      label="To Deliver"
                      :error="
                        fields.to_deliver.touched
                          ? fields.to_deliver.error
                          : null
                      "
                      @blur="fields.to_deliver.touched = true"
                    />
                  </div>
                  <div class="col-span-2">
                    <DateInput
                      v-model="fields.delivery_date.value"
                      label="delivery Date"
                      :readonly="isReadOnly"
                      :error="
                        fields.delivery_date.touched
                          ? fields.delivery_date.error
                          : null
                      "
                      @blur="fields.delivery_date.touched = true"
                    />
                  </div>
                  <div class="col-span-2">
                    <SearchSelect
                      label="Sales Rep"
                      end-point="revenue/master-data/sales-reps"
                      v-model="fields.sales_reps_id.value"
                      :readonly="isReadOnly"
                      :allow-new-record="true"
                      @select="prepareItemRow"
                      :error="
                        fields.sales_reps_id.touched
                          ? fields.sales_reps_id.error
                          : null
                      "
                      @blur="fields.sales_reps_id.touched = true"
                    />
                  </div>
                </div>
                <div class="flex justify-between items-center mb-2">
                  <RadioInput
                    option-label="full_address"
                    v-if="!isReadOnly"
                    name="delivery_address"
                    label="Delivery Address"
                    :options="customer?.addresses || []"
                    v-model="fields.delivery_address_id.value"
                    :readonly="isReadOnly"
                  />
                  <div
                    v-else
                    class="w-full p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <h4 class="font-medium text-gray-900 dark:text-white">
                      Delivery Address
                    </h4>
                    {{ formData.delivery_address?.full_address }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Right Column -->
          <div class="space-y-6 col-span-2">
            <!-- Quotation Details -->
            <div class="space-y-4">
              <div class="grid grid-cols-3 gap-4">
                <div>
                  <SearchSelect
                    label="Warehouse"
                    end-point="system/company/warehouses"
                    v-model="fields.warehouses_id.value"
                    @loaded="warehouseLoaded"
                    :readonly="isReadOnly"
                    @select="handleWarehouse"
                    :error="
                      fields.warehouses_id.touched
                        ? fields.warehouses_id.error
                        : null
                    "
                    @blur="fields.warehouses_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Price List"
                    end-point="finance/master-data/price-lists"
                    v-model="fields.price_lists_id.value"
                    @loaded="priceListLoaded"
                    :readonly="isReadOnly"
                    @select="prepareItemRow"
                    :error="
                      fields.price_lists_id.touched
                        ? fields.price_lists_id.error
                        : null
                    "
                    @blur="fields.price_lists_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Outlet"
                    end-point="system/company/outlets"
                    v-model="fields.outlets_id.value"
                    :readonly="isReadOnly"
                    @select="prepareItemRow"
                    :error="
                      fields.outlets_id.touched ? fields.outlets_id.error : null
                    "
                    @blur="fields.outlets_id.touched = true"
                  />
                </div>
              </div>

              <div class="grid grid-cols-3 gap-4">
                <div>
                  <SearchSelect
                    label="Payment Terms"
                    end-point="finance/master-data/credit-terms"
                    v-model="fields.credit_terms_id.value"
                    :readonly="isReadOnly"
                    @loaded="creditTermsLoaded"
                    @select="handlePaymetTerms"
                    :error="
                      fields.credit_terms_id.touched
                        ? fields.credit_terms_id.error
                        : null
                    "
                    @blur="fields.credit_terms_id.touched = true"
                  />
                </div>
                <div>
                  <DateInput
                    v-model="fields.due_date.value"
                    label="Duie Date"
                    :readonly="isReadOnly"
                    :error="
                      fields.due_date.touched ? fields.due_date.error : null
                    "
                    @blur="fields.due_date.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Sales Channel"
                    end-point="revenue/master-data/sales-channels"
                    v-model="fields.sales_channels_id.value"
                    @loaded="salesChannelLoaded"
                    :readonly="isReadOnly"
                    @select="prepareItemRow"
                    :error="
                      fields.sales_channels_id.touched
                        ? fields.sales_channels_id.error
                        : null
                    "
                    @blur="fields.sales_channels_id.touched = true"
                  />
                </div>
              </div>

              <div class="grid grid-cols-3 gap-4">
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Date
                  </label>
                  <input
                    v-model="formData.date_time"
                    :readonly="isReadOnly"
                    type="date"
                    class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Customer Reference No.
                  </label>
                  <input
                    v-model="fields.customer_reference.value"
                    type="text"
                    :readonly="isReadOnly"
                    class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Document Number
                  </label>
                  <input
                    v-model="fields.ref_no.value"
                    type="text"
                    readonly
                    class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <TabComponent :tabs="mobileTabs" v-model="activeTabMobile">
          <template #customer>
            <div>
              <div class="">
                <SearchSelect
                  label="Customer"
                  end-point="revenue/master-data/customers"
                  v-if="!isReadOnly"
                  v-model="fields.customers_id.value"
                  :allow-new-record="true"
                  id="customers_id"
                  :readonly="isReadOnly"
                  :remote="true"
                  :withDetails="true"
                  :error="
                    fields.customers_id.touched
                      ? fields.customers_id.error
                      : null
                  "
                  @blur="fields.customers_id.touched = true"
                  @select="handleCustomer"
                />
              </div>

              <div
                v-if="customer"
                class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <h4 class="font-medium text-gray-900 dark:text-white">
                  {{ customer?.name }}
                </h4>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  {{ customer?.email }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  {{ customer?.mobile }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  {{ customer?.address ?? customer?.building_address }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  <span>TIN: </span> {{ customer?.tax_registration_no }}
                </p>
              </div>
              <div
                v-else
                class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 text-center"
              >
                No Customer selected
              </div>
            </div>
          </template>

          <template #delivery>
            <div class="space-y-6">
              <div class="grid grid-cols-3 gap-6 mb-8">
                <div class="flex justify-between items-center mb-2 col-span-1">
                  <CheckInput
                    v-model="fields.to_deliver.value"
                    :readonly="isReadOnly"
                    label="To Deliver"
                    :error="
                      fields.to_deliver.touched ? fields.to_deliver.error : null
                    "
                    @blur="fields.to_deliver.touched = true"
                  />
                </div>
                <div class="col-span-2">
                  <DateInput
                    v-model="fields.delivery_date.value"
                    label="delivery Date"
                    :readonly="isReadOnly"
                    :error="
                      fields.delivery_date.touched
                        ? fields.delivery_date.error
                        : null
                    "
                    @blur="fields.delivery_date.touched = true"
                  />
                </div>
              </div>
              <div class="flex justify-between items-center mb-2">
                <RadioInput
                  option-label="full_address"
                  v-if="!isReadOnly"
                  name="delivery_address"
                  label="Delivery Address"
                  :options="customer?.addresses || []"
                  v-model="fields.delivery_address_id.value"
                  :readonly="isReadOnly"
                />
                <div
                  v-else
                  class="w-full p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    Delivery Address
                  </h4>
                  {{ formData.delivery_address?.full_address }}
                </div>
              </div>
            </div>
          </template>
          <template #document>
            <div class="space-y-4">
              <div class="grid md:grid-cols-3 gap-4">
                <div>
                  <SearchSelect
                    label="Warehouse"
                    end-point="system/company/warehouses"
                    v-model="fields.warehouses_id.value"
                    @loaded="warehouseLoaded"
                    :readonly="isReadOnly"
                    @select="handleWarehouse"
                    :error="
                      fields.warehouses_id.touched
                        ? fields.warehouses_id.error
                        : null
                    "
                    @blur="fields.warehouses_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Price List"
                    end-point="finance/master-data/price-lists"
                    v-model="fields.price_lists_id.value"
                    @loaded="priceListLoaded"
                    :readonly="isReadOnly"
                    @select="prepareItemRow"
                    :error="
                      fields.price_lists_id.touched
                        ? fields.price_lists_id.error
                        : null
                    "
                    @blur="fields.price_lists_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Payment Terms"
                    end-point="finance/master-data/credit-terms"
                    v-model="fields.credit_terms_id.value"
                    @loaded="creditTermsLoaded"
                    :readonly="isReadOnly"
                    @select="handlePaymetTerms"
                    :error="
                      fields.credit_terms_id.touched
                        ? fields.credit_terms_id.error
                        : null
                    "
                    @blur="fields.credit_terms_id.touched = true"
                  />
                </div>
              </div>

              <div class="grid md:grid-cols-3 gap-4">
                <div>
                  <SearchSelect
                    label="Outlet"
                    end-point="system/company/outlets"
                    v-model="fields.outlets_id.value"
                    :readonly="isReadOnly"
                    @select="prepareItemRow"
                    :error="
                      fields.outlets_id.touched ? fields.outlets_id.error : null
                    "
                    @blur="fields.outlets_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Sales Rep"
                    end-point="revenue/master-data/sales-reps"
                    v-model="fields.sales_reps_id.value"
                    :readonly="isReadOnly"
                    :allow-new-record="true"
                    @select="prepareItemRow"
                    :error="
                      fields.sales_reps_id.touched
                        ? fields.sales_reps_id.error
                        : null
                    "
                    @blur="fields.sales_reps_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Sales Channel"
                    end-point="revenue/master-data/sales-channels"
                    v-model="fields.sales_channels_id.value"
                    @loaded="salesChannelLoaded"
                    :readonly="isReadOnly"
                    @select="prepareItemRow"
                    :error="
                      fields.sales_channels_id.touched
                        ? fields.sales_channels_id.error
                        : null
                    "
                    @blur="fields.sales_channels_id.touched = true"
                  />
                </div>
              </div>

              <div class="grid md:grid-cols-3 gap-4">
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Customer Reference No.
                  </label>
                  <input
                    v-model="fields.customer_reference.value"
                    type="text"
                    :readonly="isReadOnly"
                    class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Date
                  </label>
                  <input
                    v-model="formData.date_time"
                    :readonly="isReadOnly"
                    type="date"
                    class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Document Number
                  </label>
                  <input
                    v-model="fields.ref_no.value"
                    type="text"
                    readonly
                    class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
              </div>
            </div>
          </template>
        </TabComponent>
      </template>
      <div
        class="space-y-6"
        v-if="isReadOnly && !isEmpty(formData.delivery_tracking)"
      ></div>
      <!-- Tabs -->
      <div class="mb-6" v-if="!isMobile">
        <TabComponent
          :tabs="[{ name: 'items', label: 'Items', visible: true }]"
          v-model="activeTab"
        >
          <template #buttons>
            <button
              @click="handleNewItemRow"
              v-if="!isReadOnly && !isMobile"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Add Item
            </button>
          </template>
          <template #items>
            <!-- TODO: 
                 Allow discount and change price to be dyamic from permissions
                 -->
            <ItemSalesGrid
              ref="itemGridRef"
              :items="formData.items"
              :readonly="isReadOnly"
              :ignore-batch-serial="true"
              :customer="customer"
              :currency="currency"
              :exchange_rate="formData.exchange_rate"
              :warehouses_id="formData.warehouses_id"
              :price_lists_id="formData.price_lists_id"
              :allow-change-price="
                (permissions || []).some((p) => p.permission == 'Change Price')
              "
              :allow-discount="
                (permissions || []).some((p) => p.permission == 'Allow Discount')
              "
              :allow-new-row="allowNewItemRow"
              :shelf-managed="warehouse?.shelf_managed ?? false"
              :shelfs="warehouse?.shelfs"
              @items="updateItems"
            />
            <!-- Terms and Notes -->
            <div class="grid md:grid-cols-2 gap-6">
              <div>
                <div>
                  <TextAreaInput
                    label="Remarks"
                    v-model="formData.remarks"
                    :readonly="isReadOnly"
                    :rows="7"
                    placeholder="Additional notes or remarks"
                  ></TextAreaInput>
                </div>
              </div>
              <div>
                <TextAreaInput
                  label="Internal Notes"
                  v-model="formData.remarks"
                  :rows="7"
                  :readonly="isReadOnly"
                  placeholder="Internal Notes"
                />
              </div>
            </div>
          </template>
        </TabComponent>
      </div>
      <ItemSalesGrid
        ref="itemGridRef"
        v-if="isMobile"
        :items="formData.items"
        :readonly="isReadOnly"
        :currency="formData.currency"
        :exchange_rate="formData.exchange_rate"
        :ignore-batch-serial="true"
        :customer="customer"
        :warehouses_id="formData.warehouses_id"
        :price_lists_id="formData.price_lists_id"
        :allow-change-price="false"
        :allow-discount="true"
        :allow-new-row="allowNewItemRow"
        :shelf-managed="warehouse?.shelf_managed ?? false"
        :shelfs="warehouse?.shelfs"
        @items="updateItems"
      />
    </template>
    <!-- Footer -->
    <template #footer>
      <DocumentFooter
        :data="formData"
        :readonly="isReadOnly"
        :end-point="endPoint"
        :permissions="permissions"
        :title="title"
        convert-module="Debit Note"
        @close="$emit('close')"
        @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @new="clearForm"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      />
      <SelectSalesReps
        v-if="showSelectSalesReps"
        @close="showSelectSalesReps = false"
        @select="updateSalesRep"
      />
    </template>
  </ModalFormComponent>
</template>
